import {
  MenuConfiguration,
  MenuItemConfig,
  NavigationLevel,
} from "@/types/menu";
import {
  IconAlertTriangle,
  IconBadgeFilled,
  IconBuilding,
  IconChartBar,
  IconClipboardList,
  IconDevices,
  IconGitBranch,
  IconLayoutDashboard,
  IconMap,
  IconMapPin,
  IconMapPinFilled,
  IconNotes,
  IconPalette,
  IconPhoto,
  IconPlug,
  IconSettings,
  IconShare,
  IconShield,
  IconUsers,
  IconBuildingSkyscraper,
} from "@tabler/icons-react";
import { UserRoleType } from "./constants";
import { UserRole } from "@/types/menu";

// Dynamic menu providers will be handled by the navigation system using React Query hooks
// This is just the static config now

export const MENU_CONFIG: MenuConfiguration = {
  main: [
    // Dynamic dashboards - shows user's available dashboards (grouped by 'group' field)
    // For all authenticated users
    {
      id: "dynamic-dashboards",
      type: "dynamic",
      title: "User Dashboards",
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
    // NOTE: Administration menu items are in contextual.administration
    // They are accessed via Settings in secondary menu
  ],
  secondary: [
    // Core navigation items for all users
    {
      id: "nav-sites",
      title: "Sites",
      url: "/location",
      icon: IconMapPinFilled,
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
    {
      id: "nav-devices",
      title: "Devices",
      url: "/admin/devices",
      icon: IconDevices,
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
    {
      id: "nav-alarms",
      title: "Alarms",
      url: "/admin/alarms",
      icon: IconAlertTriangle,
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
    {
      id: "nav-dashboards",
      title: "Dashboards",
      url: "/admin/dashboards",
      icon: IconLayoutDashboard,
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
    {
      id: "nav-settings",
      title: "Settings",
      url: "#",
      icon: IconSettings,
      roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
      children: [
        // Management Section
        {
          id: "section-management",
          title: "Management",
          type: "header",
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-integrators",
          title: "Integrators",
          url: "/admin/integrators",
          icon: IconBuildingSkyscraper,
          roles: [UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-organizations",
          title: "Organizations",
          url: "/admin/organizations",
          icon: IconBuilding,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-users",
          title: "Users",
          url: "/admin/users",
          icon: IconUsers,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        // Configuration Section
        {
          id: "section-configuration",
          title: "Configuration",
          type: "header",
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-device-profiles",
          title: "Device Profiles",
          url: "/admin/device-profiles",
          icon: IconDevices,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-rule-chains",
          title: "Rule Chains",
          url: "/admin/rule-chains",
          icon: IconGitBranch,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-widgets",
          title: "Widget Management",
          url: "/admin/widgets",
          icon: IconBadgeFilled,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        // System Section
        {
          id: "section-system",
          title: "System",
          type: "header",
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-integrations",
          title: "Integrations",
          url: "/admin/integrations",
          icon: IconPlug,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-branding",
          title: "Branding",
          url: "/admin/branding",
          icon: IconPhoto,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-theme-editor",
          title: "Theme Editor",
          url: "/admin/themes",
          icon: IconPalette,
          roles: [UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
        },
        {
          id: "admin-system-settings",
          title: "System Settings",
          url: "/admin/settings",
          icon: IconSettings,
          roles: [UserRoleType.SYSTEM_ADMIN],
        },
      ],
    },
  ],
  userMenu: [
    {
      id: "account-settings",
      title: "Account Settings",
      url: "/settings/account",
      icon: IconSettings,
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
    {
      id: "notifications",
      title: "Notifications",
      url: "/notifications",
      icon: IconChartBar,
      roles: [UserRoleType.ORGANIZATION_USER, UserRoleType.INTEGRATOR_ADMIN, UserRoleType.SYSTEM_ADMIN],
    },
  ],
  contextual: {
    // Define contextual menus that get generated based on navigationLevel
    // Note: Administration menu items are now in secondary menu as Settings children (accordion style)
    locations: [
      {
        id: "all-locations",
        title: "All Locations",
        url: "/location",
        icon: IconBuilding,
        description: "View and manage all location sites",
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-map",
        title: "Location Map",
        url: "/location/map",
        icon: IconMap,
        description: "View locations on a map",
        roles: [UserRoleType.ORGANIZATION_USER],
      },
    ],
    location: [
      {
        id: "location-dashboard",
        title: "Dashboard",
        url: "/location/{{locationId}}",
        icon: IconChartBar,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-details",
        title: "Details",
        url: "/location/{{locationId}}/details",
        icon: IconSettings,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-devices",
        title: "Device List",
        url: "/location/{{locationId}}/devices",
        icon: IconDevices,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-settings",
        title: "Settings",
        icon: IconSettings,
        roles: [UserRoleType.ORGANIZATION_USER],
        navigationLevel: "location-section",
        children: [
          {
            id: "settings-general",
            title: "General",
            url: "/location/{{locationId}}/settings/general",
            icon: IconSettings,
          },
          {
            id: "settings-location",
            title: "Location Info",
            url: "/location/{{locationId}}/settings/location",
            icon: IconMapPin,
          },
          {
            id: "settings-users",
            title: "Users",
            url: "/location/{{locationId}}/settings/users",
            icon: IconUsers,
          },
        ],
      },
      {
        id: "location-notes",
        title: "Notes",
        url: "/location/{{locationId}}/notes",
        icon: IconNotes,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-photos",
        title: "Photos",
        url: "/location/{{locationId}}/photos",
        icon: IconPhoto,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-share",
        title: "Share",
        url: "/location/{{locationId}}/share",
        icon: IconShare,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-alarm-logs",
        title: "Alarm Logs",
        url: "/location/{{locationId}}/alarm-logs",
        icon: IconAlertTriangle,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "location-event-logs",
        title: "Event Logs",
        url: "/location/{{locationId}}/event-logs",
        icon: IconClipboardList,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
    ],
    "location-settings": [
      {
        id: "settings-general",
        title: "General",
        url: "/location/{{locationId}}/settings/general",
        icon: IconSettings,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "settings-location",
        title: "Location Info",
        url: "/location/{{locationId}}/settings/location",
        icon: IconMapPin,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
      {
        id: "settings-users",
        title: "Users",
        url: "/location/{{locationId}}/settings/users",
        icon: IconUsers,
        roles: [UserRoleType.ORGANIZATION_USER],
      },
    ],
  },
};

// Navigation level creation - this tells the system how to create navigation levels
export const navigationLevelCreators = {
  getNavigationLevelFromPath(
    pathname: string
  ): { type: string; data?: any } | null {
    const pathSegments = pathname.split("/").filter(Boolean);

    if (pathSegments.length === 0 || pathSegments[0] === "dash") {
      return { type: "main" };
    }

    // Admin pages - all admin pages stay at main level now
    // Settings menu expands in-place as accordion, doesn't change nav level
    if (pathSegments[0] === "admin") {
      return { type: "main" };
    }

    // Legacy admin paths - also stay at main level
    if (pathSegments[0] === "system-admin" ||
        pathSegments[0] === "integrator-admin" ||
        pathSegments[0] === "tenant-admin") {
      return { type: "main" };
    }

    if (pathSegments[0] === "location") {
      // /location (sites list) is a secondary nav item - stay at main level
      if (pathSegments.length === 1) {
        return { type: "main" };
      } else if (pathSegments.length >= 2) {
        const locationId = pathSegments[1];
        if (pathSegments.length >= 3 && pathSegments[2] === "settings") {
          return {
            type: "location-section",
            data: {
              locationId,
              section: "settings",
              locationName: "Location", // Will be resolved by navigation system
            },
          };
        } else {
          return {
            type: "location",
            data: {
              locationId,
              locationName: "Location", // Will be resolved by navigation system
            },
          };
        }
      }
    }

    return { type: "main" };
  },

  createNavigationLevel(type: string, data?: any): NavigationLevel | null {
    // Find the menu config item that matches this navigation level
    const allMenuItems = [
      ...MENU_CONFIG.main,
      ...MENU_CONFIG.secondary,
      ...MENU_CONFIG.userMenu,
      ...Object.values(MENU_CONFIG.contextual).flat(),
    ];

    const findByNavigationLevel = (
      items: MenuItemConfig[],
      targetType: string
    ): MenuItemConfig | null => {
      for (const item of items) {
        if (item.navigationLevel === targetType) {
          return item;
        }
        if (item.children) {
          const found = findByNavigationLevel(item.children, targetType);
          if (found) return found;
        }
      }
      return null;
    };

    const menuItem = findByNavigationLevel(allMenuItems, type);

    if (menuItem) {
      return {
        id: menuItem.id,
        title: menuItem.title,
        icon: menuItem.icon,
        type: type as any,
        path: menuItem.url,
        data,
      };
    }

    // Fallback for specific types
    const levelMappings: Record<string, NavigationLevel> = {
      locations: {
        id: "locations",
        title: "Locations",
        type: "locations",
        path: "/location",
        icon: IconBuilding,
      },
      organizations: {
        id: "organizations",
        title: "Organizations",
        type: "organizations",
        path: "/admin/organizations",
        icon: IconUsers,
      },
      administration: {
        id: "administration",
        title: "Administration",
        type: "administration",
        path: "/admin",
        icon: IconShield,
      },
      location: {
        id: `location-${data?.locationId || "unknown"}`,
        title: data?.locationName || "Location",
        type: "location",
        path: `/location/${data?.locationId || ""}`,
        icon: IconBuilding,
        data,
      },
      "location-section": {
        id: `location-${data?.locationId || "unknown"}-${
          data?.section || "section"
        }`,
        title: data?.section ? getSectionTitle(data.section) : "Section",
        type: "location-section",
        path: `/location/${data?.locationId || ""}/${
          data?.section || ""
        }`,
        icon: IconSettings,
        data,
      },
    };

    return levelMappings[type] || null;
  },
};

// Helper function to get section title from section name
export const getSectionTitle = (section: string): string => {
  const sectionTitles: Record<string, string> = {
    dashboard: "Dashboard",
    details: "Details",
    devices: "Device List",
    settings: "Settings",
    notes: "Notes",
    photos: "Photos",
    share: "Share",
    "alarm-logs": "Alarm Logs",
    "event-logs": "Event Logs",
    edit: "Edit Location",
  };
  return (
    sectionTitles[section] || section.charAt(0).toUpperCase() + section.slice(1)
  );
};
